import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import AddIcon from '@material-ui/icons/Add';
import useDeviceDetect from '../../hooks/useDeviceDetect';
import { blueBar } from '../../helpers/common';
import { debounce } from 'lodash';
import SelectNodeLabel from '../InstantAdvanced/SelectNodeLabel';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import { getExtraInfo } from '../../services/hierarchy';
import TimeSlotSelector from '../common/TimeSlotSelector';
import DynamicSelect from '../common/DynamicSelect';
import { abbreviateVariable } from '../InstantAdvanced/helpers';

// Función para crear copia profunda que maneja fechas correctamente
const deepCloneWithDates = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepCloneWithDates(item));
  }

  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepCloneWithDates(obj[key]);
    }
  }
  return cloned;
};

let scrollsSettings = {
  overflow: 'scroll',
  overflowX: 'hidden',
};
let webkitScrollbar = {
  background: 'white',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: '#beb1b18f',
  color: 'red',
  width: '9px',
};
let webkitscrollbarTrack = {
  background: '#dfdfe957',
};
const useStyles = makeStyles(theme => ({
  root: {
    ...scrollsSettings,
    '&::-webkit-scrollbar': {
      //son los estilos de las barras scroll en una variable.
      ...webkitScrollbar,
    },
    '&::-webkit-scrollbar-track': {
      //son los estilos de las barras scroll en una variable.
      ...webkitscrollbarTrack,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
    },
    '& .MuiGrid-root': {
      padding: theme.spacing(1),
    },
    steppersFilter: {
      '&.MuiStep-horizontal': {
        paddingLeft: '0',
        paddingRight: '0',
      },
    },
  },
  label: {
    marginLeft: theme.spacing(3),
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.primary.main,
    '@media (max-width: 700px)': {
      marginLeft: theme.spacing(1),
      marginBottom: '5px',
    },
  },
  btn: {
    margin: theme.spacing(3, 1),
  },
  leftSpacing: {
    marginLeft: theme.spacing(1),
  },
  hint: {
    backgroundColor: 'rgba(0, 0, 0, 0.38)',
    color: theme.palette.common.white,
    height: theme.spacing(3),
    width: theme.spacing(3),
    minWidth: '25px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'rgb(54, 143, 193);',
    },
  },
  tick: {
    backgroundColor: theme.palette.success.light,
  },
  active: {
    backgroundColor: theme.palette.primary.main,
  },
  devicesLabel: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '0.75rem',
  },
  addBtn: {
    margin: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    height: theme.spacing(3),
    width: theme.spacing(3),
    '&:hover': {
      backgroundColor: 'rgb(54, 143, 193);',
    },
  },
  help: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '0.75rem',
    padding: theme.spacing(0.5),
  },
}));

export default function DatasetFilter({
  setShowDatasetFilter,
  datasetSelected,
  setMainObj,
  mainObj,
  indexGroupselected,
  indexDataSetSelected,
  setActiveStepMainFilter,
  objectCatalog,
  statusNodes
}) {
  const classes = useStyles();
  const { isMobile } = useDeviceDetect(990);

  const steps = [
    'Elija un dispositivo',
    'Seleccione una variable',
    'Seleccione un color',
    'Seleccione un alias',
    'Seleccione una franja horaria (Opcional)',
  ];
  const blueLine = blueBar();
  const [dataset, setDataset] = useState(datasetSelected);
  const [disabledTimeSlot, setDisabledTimeSlot] = useState(
    datasetSelected?.timeslots ? false : true
  );
  const [showDevices, setShowDevices] = useState(false);
  const [nodeLabelSelected, setNodeLabelSelected] = useState({});
  const [activeStep, setActivestep] = useState(0);
  const [workHours, setWorkHours] = useState([]);
  const [nonWorkHours, setNonWorkHours] = useState([]);
  const [isDifferentField, setIsDifferentField] = useState(false);
  const [selectedVarInGroup, setSelectedVarInGroup] = useState(null);

  useEffect(() => {
    calculateActiveStep();
    setSelectedVarInGroup(
      mainObj?.groups?.[indexGroupselected]?.datasets
        ?.filter((_, idx) => idx !== indexDataSetSelected)
        ?.find(dataset => dataset?.variable)?.variable
    );

    if (dataset?.devices?.[0]?.id) {
      getExtraInfoNode(dataset?.devices?.[0]?.id);
    }
  }, []);

  useEffect(() => {
    if (nodeLabelSelected?.selectedNodes) {
      let devices = nodeLabelSelected?.selectedNodes;
      if (nodeLabelSelected?.selectedNodes.length === 1) {
        let nodeId = nodeLabelSelected?.selectedNodes[0].id;
        getExtraInfoNode(nodeId);
      } else {
        setActivestep(0);
      }
      if (nodeLabelSelected.selectedLabel) {
        let { id, nombre } = nodeLabelSelected.selectedLabel;
        devices[0].label_id = id;
        devices[0].label_nombre = nombre;
      }
      if (!dataset.devices) {
        setActivestep(1);
      }
      setDataset(prevDataset => ({
        ...prevDataset,
        devices,
        color: devices?.length === 1 ? devices[0]?.color : '',
        alias: devices?.length === 1 ? devices[0]?.nombre : '',
        variable: null,
      }));
    }
  }, [nodeLabelSelected]);

  const getExtraInfoNode = async nodeId => {
    let extraInfo = await getExtraInfo(nodeId);
    if (extraInfo?.data?.workhours?.length > 0) {
      setWorkHours(extraInfo?.data?.workhours);
      setNonWorkHours(extraInfo?.data?.non_workhours);
    }
  };

  const handleSubmit = () => {
    if (dataset?.devices?.length > 1) {
      handleMultiplesDevices();
    } else {
      // Crear una copia profunda del mainObj para evitar mutaciones
      const updatedMainObj = deepCloneWithDates(mainObj);
      updatedMainObj.groups[indexGroupselected].datasets[indexDataSetSelected] = dataset;
      setMainObj(updatedMainObj);
    }

    setShowDatasetFilter(false);
    if (indexGroupselected === 0) {
      setActiveStepMainFilter(3);
    } else if (indexGroupselected === 1) {
      setActiveStepMainFilter(4);
    } else if (indexGroupselected === 2) {
      setActiveStepMainFilter(5);
    }
  };

  const handleMultiplesDevices = () => {
    let devices = [...dataset.devices];
    let variable = objectCatalog[dataset.variable]
    let datasets = devices.map(device => ({
      devices: [device],
      alias: `${device.nombre} - ${variable?.name || ''}`,
      color: device.color,
      timeslots: dataset.timeslots,
      variable: dataset.variable,
      granularity: 'DAILY',
    }));
    // Crear una copia profunda del mainObj para evitar mutaciones
    const updatedMainObj = deepCloneWithDates(mainObj);
    updatedMainObj.groups[indexGroupselected].datasets.push(...datasets);
    setMainObj(updatedMainObj);
  };

  const handleAlias = e => {
    setDataset({
      ...dataset,
      alias: e?.target?.value,
    });
    if (!dataset.alias) {
      setActivestep(1);
    }
  };

  const handleColorChange = debounce(value => {
    setDataset({
      ...dataset,
      color: value,
    });
    setActivestep(3);
  });

  const handleTimeSlot = timeslots => {
    setDataset(prevDataset => ({
      ...prevDataset,
      timeslots: disabledTimeSlot ? '' : timeslots,
    }));
  };

  const handleOpenCloseSelectNodeTag = () => {
    setShowDevices(!showDevices);
  };

  const handleCancel = () => {
    setShowDatasetFilter(false);
  };

  const handleDeleteDevice = index => {
    let devices = [...dataset.devices];
    devices.splice(index, 1);
    setDataset({
      ...dataset,
      devices: devices,
    });
  };

  function calculateActiveStep() {
    if (datasetSelected?.timeslots) {
      setActivestep(4);
    } else if (dataset?.color) {
      setActivestep(3);
    } else {
      setActivestep(0);
    }
  }

  const handleFilterChange = e => {
    if (e.target.name === 'variable') {
      let variable = objectCatalog[e.target.value];

      let newAlias = dataset?.alias?.split('-')[0] + ' - ' + abbreviateVariable(variable.name);

      if (selectedVarInGroup && (variable.key !== objectCatalog[selectedVarInGroup]?.key)) {
        setIsDifferentField(true);
      } else {
        setIsDifferentField(false);
      }

      setDataset(prevDataset => ({
        ...prevDataset,
        variable: e.target.value,
        granularity: 'DAILY',
        showPhase: objectCatalog[e.target.value]?.phase,
        alias: newAlias,
      }));
      if (!dataset.variable) {
        setActivestep(2);
      }
    }
  };

  return (
    <Box className={classes.root}>
      <Stepper
        className={classes.steppersFilter}
        activeStep={activeStep}
        alternativeLabel
      >
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{isMobile ? '' : label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <form width="100%">
        {blueLine}
        <Grid container style={{ padding: 8 }} width="100%">
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={2}>
              <Tooltip
                title="Elija el/los dispositivos que desea añadir al gráfico haciendo click en el botón +"
                arrow
                placement="right"
              >
                <Box
                  className={clsx(
                    classes.hint,
                    classes.leftSpacing,
                    { [classes.active]: activeStep === 0 },
                    { [classes.tick]: dataset?.devices }
                  )}
                >
                  1
                </Box>
              </Tooltip>
              <Box className={classes.devicesLabel}>Agregar dispositivo</Box>
              <IconButton
                className={classes.addBtn}
                onClick={() => {
                  setShowDevices(true);
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>

              {dataset?.devices?.map((node, index) => (
                <Chip
                  key={node.id}
                  icon={<LabelImportantIcon style={{ fontSize: '18px' }} />}
                  label={`${node.nombre}
                          ${node.label_nombre ? ` + ${node.label_nombre}` : ''
                    }`}
                  clickable
                  color="primary"
                  style={{ marginLeft: '5px' }}
                  onClick={() => handleOpenCloseSelectNodeTag('edit')}
                  onDelete={() => handleDeleteDevice(index)}
                />
              ))}
              {(!dataset.devices || dataset?.devices?.length === 0) && (
                <Box
                  ml={1}
                  mt={1}
                  className={classes.devicesLabel}
                  width={'100%'}
                >
                  Debe seleccionar al menos un dispositivo.
                </Box>
              )}
              <Box
                ml={1}
                mt={1}
                className={classes.devicesLabel}
                width={'100%'}
              >
                Nota: Si selecciona múltiples dispositivos se creará un dataset
                para cada uno.
              </Box>
            </Box>
          </Grid>

          {/* Variable */}
          <Grid item xs={12}>
            <Box display="flex" minHeight={40} alignItems="flex-end" my={2}>
              <Tooltip
                title="Elija la variable a graficar"
                arrow
                placement="right"
              >
                <Box
                  className={clsx(
                    classes.hint,
                    classes.leftSpacing,
                    {
                      [classes.active]: activeStep === 1,
                    },
                    {
                      [classes.tick]: dataset?.variable,
                    }
                  )}
                >
                  2
                </Box>
              </Tooltip>
              <DynamicSelect
                disabledSelect={false}
                disabledVarianteHistoric={false}
                handleChangeProp={handleFilterChange}
                valueSelect={dataset?.variable || ''}
                keyName={'variable'}
                InputLabelProp={'Variable principal a graficar'}
                section={'HISTORICOS'}
                placeHolder={'Variable'}
              />
            </Box>
            {!dataset.variable && (
              <Box ml={1} mt={2} className={classes.help}>
                Debe elegir una variable.
              </Box>
            )}
            {isDifferentField && (
              <Box ml={1} mt={2} className={classes.help} style={{ color: 'red' }}>
                Debe elegir la misma variable para todos los datasets del mismo grupo.
              </Box>
            )}
          </Grid>

          <Grid item xs={12} mt={2}>
            <Box display="flex" alignItems="center" flexWrap="wrap" mt={3}>
              <Tooltip title="Color" arrow placement="right">
                <Box
                  className={clsx(
                    classes.hint,
                    classes.leftSpacing,
                    { [classes.active]: activeStep === 2 },
                    { [classes.tick]: dataset?.color }
                  )}
                >
                  3
                </Box>
              </Tooltip>
              <Box className={classes.devicesLabel}>Seleccione un color</Box>
              <TextField
                style={{ width: '40px', marginLeft: '15px' }}
                type="color"
                value={dataset?.color || ''}
                name="color1"
                onChange={e => handleColorChange(e.target.value)}
                disabled={
                  dataset?.devices?.length > 1 ||
                  !dataset.devices ||
                  dataset.devices.length === 0
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} mt={2}>
            <Box display="flex" alignItems="center" mt={3}>
              <Tooltip title="Alias" arrow placement="right">
                <Box
                  className={clsx(
                    classes.hint,
                    classes.leftSpacing,
                    { [classes.active]: activeStep === 3 },
                    { [classes.tick]: dataset?.alias }
                  )}
                >
                  4
                </Box>
              </Tooltip>
              <TextField
                style={{ width: '84%' }}
                autoFocus
                placeholder="Alias"
                name="name"
                onChange={e => {
                  handleAlias(e);
                }}
                value={dataset?.alias || ''}
                color="primary"
                disabled={
                  dataset?.devices?.length > 1 ||
                  !dataset.devices ||
                  dataset.devices.length === 0
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} mt={1}>
            <Box display="flex" alignItems="start" flexWrap="wrap" mt={2}>
              <Tooltip title="Franja horaria" arrow placement="right">
                <Box
                  marginTop={1}
                  className={clsx(
                    classes.hint,
                    classes.leftSpacing,
                    { [classes.active]: activeStep === 4 },
                    { [classes.tick]: dataset.timeslots }
                  )}
                >
                  5
                </Box>
              </Tooltip>
              <Box display={'flex'} alignItems={'center'}>
                <Box className={classes.devicesLabel} mr={1}>
                  Franja horaria
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      name="showSecondary"
                      checked={!disabledTimeSlot}
                      onChange={() => setDisabledTimeSlot(!disabledTimeSlot)}
                      color="primary"
                      disabled={
                        !dataset.devices || dataset.devices.length === 0
                      }
                    />
                  }
                />
              </Box>
            </Box>

            <Box ml={2}>
              <Box mt={1} display={'flex'}>
                <TimeSlotSelector
                  timeSlots={dataset.timeslots}
                  disabledTimeSlot={disabledTimeSlot}
                  handleSetTimeSlots={handleTimeSlot}
                  workHours={workHours}
                  nonWorkHours={nonWorkHours}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {blueLine}

        <Box display={'flex'} justifyContent={'center'} width={'100%'}>
          <Button
            variant="contained"
            color="primary"
            className={classes.btn}
            onClick={handleSubmit}
            disabled={
              (dataset?.devices?.length === 1 &&
                (!dataset?.devices || !dataset?.alias || !dataset.variable)) ||
              !dataset?.devices ||
              dataset?.devices.length === 0 ||
              !dataset.variable || isDifferentField
            }
          >
            Aceptar
          </Button>

          <Button
            variant="contained"
            onClick={handleCancel}
            className={classes.btn}
          >
            Cancelar
          </Button>
        </Box>
      </form>
      {showDevices && (
        <SelectNodeLabel
          showDevices={showDevices}
          setNodeLabelSelected={setNodeLabelSelected}
          handleOpenCloseSelectNodeTag={handleOpenCloseSelectNodeTag}
          multi={true}
          statusNodes={statusNodes}
        />
      )}
    </Box>
  );
}
